import PageGlow from "../components/PageGlow";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookmarkX, ArrowUpRight, BadgeCheck, Upload, Sparkles, Printer, Bell, BellOff, Trash2, Info, Download } from "lucide-react";
import { api, formatApiError, uploadFile, timeAgo } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import JobCard from "../components/JobCard";
import { sourceMeta } from "../lib/sources";
import NotificationPrefs from "../components/NotificationPrefs";
import ChatThread from "../components/ChatThread";
import MessagesInbox from "../components/MessagesInbox";
import AccountVerification from "../components/AccountVerification";
import EmailConfirmBanner from "../components/EmailConfirmBanner";
import CandidateInterviews from "../components/CandidateInterviews";
import InstallAppPrompt from "../components/InstallAppPrompt";
import { MobileMenuHome, MobileBackHeader } from "../components/MobileDashMenu";
import Pager, { pageSlice } from "../components/Pager";
import CandidateMatching from "../components/CandidateMatching";

const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700",
  reviewed: "bg-zinc-100 text-zinc-700",
  shortlisted: "bg-green-50 text-green-700",
  rejected: "bg-red-50 text-red-700",
};

export default function CandidateDashboard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => (typeof window !== "undefined" && window.innerWidth < 640 ? "menu" : "applications"));
  const [applications, setApplications] = useState([]);
  const [saved, setSaved] = useState([]);
  const [searches, setSearches] = useState([]);
  const [profile, setProfile] = useState({ name: "", headline: "", phone: "", location: "", cv_link: "", about: "", looking_for: "", skillsText: "", availability: "", salary_expectation: "", preferredLocationsText: "", experience: [], education: [] });
  const [saving, setSaving] = useState(false);
  const [aboutBusy, setAboutBusy] = useState(false);
  const [activity, setActivity] = useState([]);
  const [appPage, setAppPage] = useState(1);
  const [savedPage, setSavedPage] = useState(1);
  const [actPage, setActPage] = useState(1);
  const [alertPage, setAlertPage] = useState(1);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [chatFor, setChatFor] = useState(null);
  // per-user draft: unsaved profile edits survive leaving the page; cleared on save
  const profileDraftKey = user?.id ? `ht_profile_draft_${user.id}` : null;
  const serverProfile = useRef(null);
  const pendingDraft = useRef(null);    // parsed draft kept until an explicit save
  const draftLoaded = useRef(false);
  const draftToastShown = useRef(false);
  const printGuard = useRef(false);

  // one click = one print dialog, even if the event is delivered twice
  const printCv = () => {
    if (printGuard.current) return;
    printGuard.current = true;
    window.print();
    setTimeout(() => { printGuard.current = false; }, 500);
  };

  const PROFILE_KEYS = ["name", "headline", "phone", "location", "cv_link", "about",
    "looking_for", "skillsText", "availability", "salary_expectation", "preferredLocationsText"];

  useEffect(() => {
    api.get("/candidate/applications").then((res) => setApplications(res.data)).catch(() => {});
    api.get("/candidate/saved").then((res) => setSaved(res.data)).catch(() => {});
    api.get("/candidate/searches").then((res) => setSearches(res.data)).catch(() => {});
    api.get("/candidate/activity").then((res) => setActivity(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    const base = {
      name: user.name || "", headline: user.headline || "", phone: user.phone || "",
      location: user.location || "", cv_link: user.cv_link || "", about: user.about || "",
      looking_for: user.looking_for || "", skillsText: (user.skills || []).join(", "),
      availability: user.availability || "", salary_expectation: user.salary_expectation || "",
      preferredLocationsText: (user.preferred_locations || []).join(", "),
      experience: (user.experience || []).map((x, i) => ({ ...x, _k: `saved-${i}-${(x.title || "").length}-${(x.company || "").length}` })),
      education: (user.education || []).map((x, i) => ({ ...x, _k: `edu-${i}-${(x.title || "").length}` })),
    };
    serverProfile.current = base;
    // StrictMode-safe: the parsed draft is stored in a ref and re-applied on every
    // hydration pass until the user explicitly saves, never a one-shot flag.
    if (!draftLoaded.current && profileDraftKey) {
      draftLoaded.current = true;
      try {
        const raw = localStorage.getItem(profileDraftKey);
        if (raw) {
          const d = JSON.parse(raw);
          if (PROFILE_KEYS.some((k) => (d[k] ?? "") !== base[k])) pendingDraft.current = d;
        }
      } catch { /* ignore corrupt drafts */ }
    }
    if (pendingDraft.current) {
      setProfile({ ...base, ...pendingDraft.current });
      if (!draftToastShown.current) {
        draftToastShown.current = true;
        toast.info("Unsaved profile changes from your last visit were restored.");
      }
    } else {
      setProfile(base);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/profile", {
        ...profile,
        skills: profile.skillsText.split(",").map((s) => s.trim()).filter(Boolean),
        preferred_locations: profile.preferredLocationsText.split(",").map((s) => s.trim()).filter(Boolean),
        experience: profile.experience.filter((x) => x.title && x.title.trim()).map(({ _k, ...rest }) => rest),
        education: profile.education.filter((x) => x.title && x.title.trim()).map(({ _k, ...rest }) => rest),
      });
      await refreshUser();
      pendingDraft.current = null;
      if (profileDraftKey) { try { localStorage.removeItem(profileDraftKey); } catch { /* ignore */ } }
      // keep the archived CV copy current too (stored by HiredToday, survives account deletion)
      api.post("/candidate/cv/pdf", {}, { responseType: "blob" }).catch(() => {});
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const downloadCvPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.post("/candidate/cv/pdf", {}, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(profile.name || user?.name || "cv").split(" ")[0].toLowerCase()}-hiredtoday-cv.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPdfBusy(false);
    }
  };

  const setExp = (i, k, v) => setProfile((p) => ({
    ...p,
    experience: p.experience.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)),
  }));

  const setEdu = (i, k, v) => setProfile((p) => ({
    ...p,
    education: p.education.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)),
  }));

  // autosave the draft, but only while it actually differs from what's on the server;
  // a pending (unsaved) draft is never deleted by the autosave, only by an explicit save
  useEffect(() => {
    if (!profileDraftKey || !serverProfile.current) return undefined;
    const t = setTimeout(() => {
      try {
        const dirty = PROFILE_KEYS.some((k) => (profile[k] ?? "") !== serverProfile.current[k]);
        if (dirty) {
          pendingDraft.current = { ...profile };
          localStorage.setItem(profileDraftKey, JSON.stringify(pendingDraft.current));
        } else if (!pendingDraft.current) {
          localStorage.removeItem(profileDraftKey);
        }
      } catch { /* best-effort */ }
    }, 600);
    return () => clearTimeout(t);
  }, [profile, profileDraftKey]);

  const [idDoc, setIdDoc] = useState(null);
  const [idUploading, setIdUploading] = useState(false);
  const [idSaving, setIdSaving] = useState(false);
  const [cvUploading, setCvUploading] = useState(false);

  const uploadCv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCvUploading(true);
    try {
      const r = await uploadFile(file, "cv");
      await api.put("/auth/profile", { cv_path: r.path, cv_filename: r.filename });
      await refreshUser();
      toast.success("CV saved. We'll attach it to your applications");
    } catch (err) {
      toast.error(err.message || formatApiError(err));
    } finally {
      setCvUploading(false);
      e.target.value = "";
    }
  };

  const uploadId = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIdUploading(true);
    try {
      const r = await uploadFile(file, "id");
      setIdDoc({ path: r.path, filename: r.filename, kind: "id" });
      toast.success(`${file.name} attached`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIdUploading(false);
      e.target.value = "";
    }
  };

  const submitId = async () => {
    if (!idDoc) return;
    setIdSaving(true);
    try {
      await api.post("/verification/submit", { documents: [idDoc] });
      await refreshUser();
      toast.success("ID submitted. We'll review it and confirm by email.");
      setIdDoc(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setIdSaving(false);
    }
  };

  const unsave = async (jobId) => {
    try {
      await api.delete(`/candidate/saved/${jobId}`);
      setSaved((s) => s.filter((j) => j.id !== jobId));
      toast.success("Removed from saved jobs");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const strengthChecks = [
    ["a headline", !!(profile.headline || "").trim()],
    ["your location", !!(profile.location || "").trim()],
    ["your phone number", !!(profile.phone || "").trim()],
    ["your availability", !!profile.availability],
    ["what you're looking for", !!(profile.looking_for || "").trim()],
    ["a short about section", !!(profile.about || "").trim()],
    ["your skills", !!(profile.skillsText || "").trim()],
    ["your experience", (profile.experience || []).length > 0],
    ["your education", (profile.education || []).length > 0],
    ["your salary expectation", !!(profile.salary_expectation || "").trim()],
  ];
  const profileStrength = Math.round((strengthChecks.filter(([, ok]) => ok).length / strengthChecks.length) * 100);

  const draftAbout = async () => {
    setAboutBusy(true);
    try {
      const r = await api.post("/ai/profile-summary", {
        headline: profile.headline, location: profile.location, looking_for: profile.looking_for,
        skills: profile.skillsText, experience: profile.experience, education: profile.education,
        about: profile.about,
      });
      setProfile((p) => ({ ...p, about: r.data.summary }));
      toast.success("Draft added, edit it until it sounds like you.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAboutBusy(false);
    }
  };

  const [msgUnread, setMsgUnread] = useState(0);
  useEffect(() => {
    const load = () => api.get("/messages/unread-count").then((r) => setMsgUnread(r.data.unread || 0)).catch(() => {});
    const onRead = (e) => setMsgUnread((n) => Math.max(0, n - (e.detail?.cleared || 0)));
    window.addEventListener("ht:msg-read", onRead);
    load();
    const t = setInterval(load, 30000);
    return () => { clearInterval(t); window.removeEventListener("ht:msg-read", onRead); };
  }, []);

  const tabs = [
    { id: "applications", label: `My applications (${applications.length})` },
    { id: "interviews", label: "Interviews" },
    { id: "messages", label: msgUnread > 0 ? (<>Messages <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-600 text-white" data-testid="cand-tab-msg-badge">{msgUnread}</span></>) : "Messages" },
    { id: "saved", label: `Saved jobs (${saved.length})` },
    { id: "activity", label: "Job activity" },
    { id: "alerts", label: `Job alerts (${searches.length})` },
    { id: "matching", label: "AI matching" },
    { id: "profile", label: "My profile" },
  ];

  const toggleSearchAlert = async (s) => {
    try {
      await api.put(`/candidate/searches/${s.id}`, {
        query: s.query, location: s.location, category: s.category,
        job_type: s.job_type, workplace: s.workplace, alerts_on: !s.alerts_on,
      });
      setSearches((arr) => arr.map((x) => (x.id === s.id ? { ...x, alerts_on: !s.alerts_on } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteSearch = async (id) => {
    try {
      await api.delete(`/candidate/searches/${id}`);
      setSearches((arr) => arr.filter((x) => x.id !== id));
      toast.success("Saved search removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const withdraw = async (appId) => {
    try {
      await api.delete(`/candidate/applications/${appId}`);
      setApplications((arr) => arr.filter((x) => x.id !== appId));
      toast.success("Application withdrawn. The employer has been told.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const requestDeletion = async () => {
    setDeleteBusy(true);
    try {
      await api.post("/privacy/delete-account");
      await refreshUser();
      setConfirmDelete(false);
      toast.success("Deletion scheduled. You have 30 days to change your mind.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  const cancelDeletion = async () => {
    try {
      await api.post("/privacy/cancel-deletion");
      await refreshUser();
      toast.success("Deletion cancelled. Your account stays as it is.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const [talentActivity, setTalentActivity] = useState(null);
  const [talentBusy, setTalentBusy] = useState("");
  const [privacyLocal, setPrivacyLocal] = useState(null);

  useEffect(() => {
    if (tab === "profile") {
      api.get("/candidate/talent-activity").then((r) => setTalentActivity(r.data)).catch(() => {});
    }
  }, [tab, user?.discoverable]);

  const decideAccess = async (id, decision) => {
    setTalentBusy(id);
    try {
      await api.post(`/candidate/talent-access/${id}/decide`, { decision });
      const r = await api.get("/candidate/talent-activity");
      setTalentActivity(r.data);
      toast.success(decision === "approved" ? "Details shared with the employer" : "Request declined, nothing was shared");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTalentBusy("");
    }
  };

  const setPrivacy = async (patch) => {
    try {
      await api.put("/auth/profile", patch);
      await refreshUser();
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <main className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="candidate-dashboard">
      <InstallAppPrompt />
      <PageGlow subtle />
      <EmailConfirmBanner />
      <Seo title="My Dashboard | HiredToday" description="Your HiredToday candidate dashboard." path="/dashboard" />
      <div className="hidden sm:flex items-center gap-2">
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-zinc-950">
          Hello, {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <span className="group relative inline-flex mt-1" data-testid="dashboard-info">
          <Info className="h-4 w-4 text-zinc-400 cursor-help" />
          <span className="pointer-events-none absolute right-0 sm:right-auto sm:left-0 top-full z-20 mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-md bg-zinc-950 px-3 py-2 text-xs leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">Everything you're tracking lives here: applications, interviews, saved jobs, alerts and your profile.</span>
        </span>
      </div>

      <div className="hidden sm:flex mt-4 flex-wrap items-center gap-3">
        <Link to="/jobs" data-testid="find-jobs-cta"
          className="inline-flex items-center gap-2 h-10 px-5 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">
          Find jobs <ArrowUpRight className="h-4 w-4" />
        </Link>
        <p className="text-xs text-zinc-400">Your account, saved searches and alerts stay exactly where you left them.</p>
      </div>

      <div className="hidden sm:flex gap-1 mt-8 border-b border-zinc-200 overflow-x-auto" data-testid="candidate-tabs">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors duration-200 ${
              tab === t.id ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "menu" && (
        <MobileMenuHome
          name={user?.name || "Jobseeker"}
          stat={{ label: "Open applications", value: String(applications.length), hint: `${saved.length} saved jobs · ${searches.length} alerts on` }}
          items={[{ id: "findjobs", label: "Find jobs", badge: 0 }, ...tabs.map((t) => ({ id: t.id, label: typeof t.label === "string" ? t.label : "Messages", badge: t.id === "messages" ? msgUnread : 0 }))]}
          onSelect={(id) => id === "findjobs" ? navigate("/jobs") : setTab(id)}
        />
      )}
      {tab !== "menu" && (
        <MobileBackHeader
          title={(() => { const l = tabs.find((t) => t.id === tab)?.label; return typeof l === "string" ? l : "Messages"; })()}
          onBack={() => setTab("menu")}
        />
      )}

      {user?.deletion_scheduled_for && (
        <div className="mt-6 border border-amber-300 bg-amber-50 rounded-md p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="deletion-banner">
          <p className="text-sm text-amber-900">
            Your account is scheduled for deletion on {new Date(user.deletion_scheduled_for).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.
          </p>
          <button onClick={cancelDeletion} data-testid="cancel-deletion-button"
            className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 whitespace-nowrap">
            Cancel deletion
          </button>
        </div>
      )}

      <div className="mt-6 sm:mt-8">
        {tab === "applications" && (
          applications.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="no-applications">
              <p className="font-display text-xl font-bold text-zinc-900">No applications yet.</p>
              <p className="mt-2 text-sm text-zinc-500">When you apply for a role, it shows up here so you can keep track.</p>
              <Link to="/jobs" data-testid="browse-jobs-cta"
                className="mt-6 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-800 transition-colors duration-200">
                Find a job <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="applications-list">
              {pageSlice(applications, appPage).map((a) => (
                <div key={a.id} className="border border-zinc-200 rounded-md p-5 bg-white flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 justify-between"
                  data-testid={`application-${a.id}`}>
                  <div>
                    <Link to={`/jobs/${a.job_slug}`} className="font-display font-bold text-zinc-950 hover:text-blue-700 transition-colors duration-200">
                      {a.job_title}
                    </Link>
                    <p className="text-sm text-zinc-500 mt-0.5">{a.company_name} · applied {new Date(a.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>
                  </div>
                  <div className="flex items-center gap-1 self-start sm:self-center">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[a.status] || STATUS_STYLES.new}`}
                      data-testid={`application-status-${a.id}`}>
                      {a.status === "new" ? "Sent" : a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                    </span>
                    <button onClick={() => withdraw(a.id)} data-testid={`withdraw-${a.id}`}
                      className="text-xs font-semibold text-zinc-400 hover:text-red-600 transition-colors duration-200 px-2 py-1">
                      Withdraw
                    </button>
                  </div>
                  {chatFor === a.id ? (
                    <div className="w-full sm:basis-full mt-1">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">Chat with {a.company_name}</p>
                        <button onClick={() => setChatFor(null)} data-testid={`chat-close-${a.id}`}
                          className="text-xs text-zinc-400 hover:text-zinc-700">Close</button>
                      </div>
                      <ChatThread applicationId={a.id} viewerRole="candidate" testid={`chat-${a.id}`} />
                    </div>
                  ) : (
                    <button onClick={() => setChatFor(a.id)} data-testid={`chat-open-${a.id}`}
                      className="self-start text-xs font-semibold text-blue-700 hover:text-blue-900 transition-colors duration-200">
                      Message employer
                    </button>
                  )}
                </div>
              ))}
              <Pager page={appPage} total={applications.length} onChange={setAppPage} testid="applications-pager" />
            </div>
          )
        )}

        {tab === "saved" && (
          saved.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="no-saved-jobs">
              <p className="font-display text-xl font-bold text-zinc-900">Nothing saved yet.</p>
              <p className="mt-2 text-sm text-zinc-500">Tap "Save for later" on any job and it'll wait for you here.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="saved-jobs-list">
              {pageSlice(saved, savedPage).map((job) => (
                <div key={job.id} className="relative">
                  <JobCard job={job} />
                  <button onClick={() => unsave(job.id)} data-testid={`unsave-${job.slug}`}
                    className="absolute top-3 right-3 p-2 text-zinc-400 hover:text-red-600 transition-colors duration-200" aria-label="Remove">
                    <BookmarkX className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <Pager page={savedPage} total={saved.length} onChange={setSavedPage} testid="saved-jobs-pager" />
            </div>
          )
        )}

        {tab === "activity" && (
          activity.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="no-activity">
              <p className="font-display text-xl font-bold text-zinc-900">No activity yet.</p>
              <p className="mt-2 text-sm text-zinc-500">Browse jobs and anything you view or apply for will show up here, so you can pick up where you left off.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="activity-list">
              {pageSlice(activity, actPage).map((a, i) => (
                <Link key={a.job_ref} to={a.url || "/jobs"} data-testid={`activity-row-${i}`}
                  className="flex items-center gap-3 bg-white border border-zinc-200 rounded-md p-4 hover:border-zinc-300 transition-colors duration-200">
                  <span data-testid={`activity-kind-${i}`}
                    className={`shrink-0 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                      a.kind === "click" ? "bg-green-50 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                    {a.kind === "click" ? "Apply clicked" : "Viewed"}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-zinc-950 truncate">{a.title}</span>
                    <span className="block text-sm text-zinc-500 truncate">{[a.company, a.location].filter(Boolean).join(" · ")}</span>
                  </span>
                  {a.source !== "native" && (
                    <span className="shrink-0 text-[11px] font-bold uppercase tracking-wider bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded"
                      data-testid={`activity-source-${i}`}>
                      {sourceMeta(a.source).label}
                    </span>
                  )}
                  <span className="shrink-0 text-xs text-zinc-400">{timeAgo(a.at)}</span>
                </Link>
              ))}
              <Pager page={actPage} total={activity.length} onChange={setActPage} testid="activity-pager" />
            </div>
          )
        )}

        {tab === "messages" && (
          <div className="mt-6" data-testid="candidate-messages-tab">
            <MessagesInbox role="candidate" testid="cand-messages" />
          </div>
        )}
        {tab === "interviews" && (
          <div className="space-y-6">
            <CandidateInterviews />
            <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="talent-activity-card">
              <p className="text-sm font-semibold text-zinc-950">Who's contacted you</p>
              <p className="text-xs text-zinc-500 mt-1 mb-3">
                Every access request and invite from employers, in one place. Profiles you've used in applications
                stay private, only opting in to discoverability makes you visible.
                {talentActivity?.consent_since && ` You opted in on ${new Date(talentActivity.consent_since).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.`}
              </p>
              {!talentActivity ? (
                <div className="h-16 rounded-md bg-zinc-50 animate-pulse" />
              ) : talentActivity.access_requests.length === 0 && talentActivity.invites.length === 0 ? (
                <p className="text-xs text-zinc-400" data-testid="talent-activity-empty">No employer contact yet. When a company asks for your details or invites you to apply, it appears here and you decide.</p>
              ) : (
                <ul className="space-y-2.5">
                  {talentActivity.access_requests.map((r) => (
                    <li key={r.id} className="border border-zinc-200 rounded-md px-4 py-3" data-testid={`access-request-${r.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-zinc-900">{r.company_name} requested your contact details</p>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                          r.status === "approved" ? "bg-green-100 text-green-700" : r.status === "declined" ? "bg-zinc-100 text-zinc-500" : "bg-amber-100 text-amber-700"}`}
                          data-testid={`access-status-${r.id}`}>{r.status}</span>
                      </div>
                      <p className="text-xs text-zinc-400 mt-0.5">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}</p>
                      {r.message && <p className="text-xs text-zinc-600 mt-1.5 border-l-2 border-zinc-200 pl-3">{r.message}</p>}
                      {r.status === "pending" && (
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => decideAccess(r.id, "approved")} disabled={talentBusy === r.id} data-testid={`access-approve-${r.id}`}
                            className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200">
                            Share my details
                          </button>
                          <button onClick={() => decideAccess(r.id, "declined")} disabled={talentBusy === r.id} data-testid={`access-decline-${r.id}`}
                            className="h-9 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                            No thanks
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                  {talentActivity.invites.map((inv) => (
                    <li key={`${inv.company_name}-${inv.job_slug}-${inv.created_at}`} className="border border-zinc-200 rounded-md px-4 py-3 text-sm" data-testid={`talent-invite-item-${inv.job_slug || inv.created_at}`}>
                      <span className="font-semibold text-zinc-900">{inv.company_name}</span>
                      <span className="text-zinc-600"> invited you to apply for </span>
                      {inv.job_slug ? (
                        <Link to={`/jobs/${inv.job_slug}`} className="font-semibold text-blue-700 hover:text-blue-900">{inv.job_title}</Link>
                      ) : <span className="font-semibold text-zinc-900">{inv.job_title}</span>}
                      <p className="text-xs text-zinc-400 mt-0.5">{inv.created_at ? new Date(inv.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === "matching" && (
          <div className="mt-6" data-testid="candidate-matching-tab">
            <CandidateMatching user={user} />
          </div>
        )}
        {tab === "alerts" && (
          searches.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center" data-testid="no-saved-searches">
              <p className="font-display text-xl font-bold text-zinc-900">No saved searches yet.</p>
              <p className="mt-2 text-sm text-zinc-500">Run a search on the jobs page, then tap "Save search & get alerts". We'll email you new matches daily.</p>
              <Link to="/jobs" data-testid="search-jobs-cta"
                className="mt-6 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-800 transition-colors duration-200">
                Search jobs <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="saved-searches-list">
              {pageSlice(searches, alertPage).map((s) => {
                const parts = [s.query, s.location, s.category, s.job_type, s.workplace].filter(Boolean);
                return (
                  <div key={s.id} className="border border-zinc-200 rounded-md p-5 bg-white flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
                    data-testid={`saved-search-${s.id}`}>
                    <div>
                      <p className="font-display font-bold text-zinc-950">{parts.join(" · ") || "All jobs"}</p>
                      <p className="text-sm text-zinc-500 mt-0.5" data-testid={`alert-state-${s.id}`}>
                        {s.alerts_on ? "Daily email alerts on" : "Alerts paused"}
                        {s.last_sent_at ? ` · last sent ${new Date(s.last_sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 self-start sm:self-center">
                      <button onClick={() => toggleSearchAlert(s)} data-testid={`toggle-alert-${s.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-300 rounded-md text-sm font-medium text-zinc-700 hover:border-zinc-400 transition-colors duration-200">
                        {s.alerts_on ? <><BellOff className="h-4 w-4" /> Pause alerts</> : <><Bell className="h-4 w-4" /> Resume alerts</>}
                      </button>
                      <button onClick={() => deleteSearch(s.id)} data-testid={`delete-search-${s.id}`} aria-label="Delete saved search"
                        className="p-2 text-zinc-400 hover:text-red-600 transition-colors duration-200">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
              <Pager page={alertPage} total={searches.length} onChange={setAlertPage} testid="saved-searches-pager" />
            </div>
          )
        )}

        {tab === "profile" && (
          <div className="max-w-2xl mt-10">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Profile</p>
          <div className="border border-zinc-200 rounded-md p-5 bg-white mb-4" data-testid="profile-strength">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-zinc-950">Profile strength</p>
              <p className="text-sm font-bold text-zinc-950" data-testid="profile-strength-score">{profileStrength}%</p>
            </div>
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-violet-600 rounded-full transition-all duration-500" style={{ width: `${profileStrength}%` }} />
            </div>
            {profileStrength < 100 && (
              <p className="text-xs text-zinc-500 mt-2">Add {strengthChecks.filter(([, ok]) => !ok).slice(0, 2).map(([label]) => label).join(" and ")} to strengthen your profile.</p>
            )}
            <div className="mt-4 pt-4 border-t border-zinc-100 grid sm:grid-cols-3 gap-3 text-xs" data-testid="profile-at-a-glance">
              <div>
                <p className="font-semibold text-zinc-950">Email</p>
                <p className="text-zinc-500 mt-0.5">{user?.email_verified ? "Verified" : "Needs confirming, see below"}</p>
              </div>
              <div>
                <p className="font-semibold text-zinc-950">Mobile</p>
                <p className="text-zinc-500 mt-0.5">Optional, text verification is being set up</p>
              </div>
              <div>
                <p className="font-semibold text-zinc-950">CV</p>
                <p className="text-zinc-500 mt-0.5">{user?.cv_filename ? `${user.cv_filename}, attached to applications` : "Builds itself from your profile below"}</p>
                <button type="button" onClick={printCv} data-testid="glance-cv-print"
                  className="mt-1.5 text-blue-700 font-semibold hover:text-blue-900 transition-colors duration-200">Print / save as PDF</button>
              </div>
            </div>
          </div>
          <AccountVerification />
          <form onSubmit={saveProfile} className="space-y-5" data-testid="profile-form">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 pt-1">Your details</p>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Full name</label>
              <input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Phone</label>
              <input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Where you're based</label>
              <input data-testid="profile-location" value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                placeholder="e.g. Manchester"
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 pt-3">The work you want</p>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Headline</label>
              <input data-testid="profile-headline" value={profile.headline} onChange={(e) => setProfile({ ...profile, headline: e.target.value })}
                placeholder="e.g. Warehouse operative with 4 years' experience"
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <p className="text-xs text-zinc-400 mt-1.5">One line employers see first on your applications.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Availability</label>
                <select data-testid="profile-availability" value={profile.availability} onChange={(e) => setProfile({ ...profile, availability: e.target.value })}
                  className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 text-zinc-700">
                  <option value="">Select…</option>
                  {["Immediate", "Within 1 week", "Within 2 weeks", "Within 1 month", "More than 1 month"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1.5">Salary expectation</label>
                <input data-testid="profile-salary" value={profile.salary_expectation} onChange={(e) => setProfile({ ...profile, salary_expectation: e.target.value })}
                  placeholder="e.g. £28,000 a year"
                  className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Preferred locations</label>
              <input data-testid="profile-preferred-locations" value={profile.preferredLocationsText} onChange={(e) => setProfile({ ...profile, preferredLocationsText: e.target.value })}
                placeholder="e.g. Leeds, Bradford, Remote"
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <p className="text-xs text-zinc-400 mt-1.5">Separate with commas. Employers see these with your applications.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">What you're looking for</label>
              <input data-testid="profile-looking-for" value={profile.looking_for} onChange={(e) => setProfile({ ...profile, looking_for: e.target.value })}
                placeholder="e.g. Full-time driving work, nights, West Midlands"
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 pt-3">About you &amp; your experience</p>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-zinc-700">About you</label>
                <button type="button" onClick={draftAbout} disabled={aboutBusy} data-testid="profile-ai-summary"
                  className="text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1 transition-colors duration-200 disabled:opacity-50">
                  <Sparkles className="h-3.5 w-3.5" /> {aboutBusy ? "Drafting…" : "Draft it for me"}
                </button>
              </div>
              <textarea rows={4} data-testid="profile-about" value={profile.about} onChange={(e) => setProfile({ ...profile, about: e.target.value })}
                placeholder="A few honest sentences. What you've done, what you're good at."
                className="w-full px-4 py-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y" />
              <p className="text-xs text-zinc-400 mt-1.5">Drafted only from the details you've given, always check and edit it.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Skills</label>
              <input data-testid="profile-skills" value={profile.skillsText} onChange={(e) => setProfile({ ...profile, skillsText: e.target.value })}
                placeholder="e.g. Forklift licence, FLT counterbalance, picking & packing"
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <p className="text-xs text-zinc-400 mt-1.5">Separate with commas.</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-zinc-700">Experience</label>
                <button type="button" data-testid="profile-add-experience"
                  onClick={() => setProfile((p) => ({ ...p, experience: [...p.experience, { title: "", company: "", period: "", _k: `new-${Date.now()}` }] }))}
                  className="text-xs font-semibold text-blue-700 hover:text-blue-900 transition-colors duration-200">
                  + Add a role
                </button>
              </div>
              {profile.experience.length === 0 && (
                <p className="text-xs text-zinc-400">No roles added yet. Your work history, most recent first.</p>
              )}
              <div className="space-y-3">
                {profile.experience.map((row, i) => (
                  <div key={row._k || i} className="border border-zinc-200 rounded-md p-4 space-y-2.5" data-testid={`experience-row-${i}`}>
                    <div className="grid sm:grid-cols-2 gap-2.5">
                      <input value={row.title} onChange={(e) => setExp(i, "title", e.target.value)}
                        placeholder="Job title" data-testid={`experience-title-${i}`}
                        className="h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                      <input value={row.company} onChange={(e) => setExp(i, "company", e.target.value)}
                        placeholder="Company" data-testid={`experience-company-${i}`}
                        className="h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    </div>
                    <div className="flex gap-2.5">
                      <input value={row.period} onChange={(e) => setExp(i, "period", e.target.value)}
                        placeholder="e.g. 2021 – 2024" data-testid={`experience-period-${i}`}
                        className="flex-1 h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                      <button type="button" onClick={() => setProfile((p) => ({ ...p, experience: p.experience.filter((_, idx) => idx !== i) }))}
                        data-testid={`experience-remove-${i}`}
                        className="h-11 px-4 border border-zinc-300 rounded-md text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors duration-200">
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 pt-3">Education</p>
            <div className="border border-zinc-200 rounded-md p-5 bg-white">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-zinc-700">Education</label>
                <button type="button" data-testid="cv-add-education"
                  onClick={() => setProfile((p) => ({ ...p, education: [...p.education, { title: "", place: "", period: "", _k: `edu-${Date.now()}` }] }))}
                  className="text-xs font-semibold text-blue-700 hover:text-blue-900 transition-colors duration-200">
                  + Add education
                </button>
              </div>
              {profile.education.length === 0 && (
                <p className="text-xs text-zinc-400">Optional. Qualifications, courses, certificates.</p>
              )}
              <div className="space-y-3 mt-2">
                {profile.education.map((row, i) => (
                  <div key={row._k || i} className="border border-zinc-200 rounded-md p-4 space-y-2.5" data-testid={`education-row-${i}`}>
                    <div className="grid sm:grid-cols-2 gap-2.5">
                      <input value={row.title} onChange={(e) => setEdu(i, "title", e.target.value)}
                        placeholder="Qualification" data-testid={`education-title-${i}`}
                        className="h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                      <input value={row.place} onChange={(e) => setEdu(i, "place", e.target.value)}
                        placeholder="School / provider" data-testid={`education-place-${i}`}
                        className="h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                    </div>
                    <div className="flex gap-2.5">
                      <input value={row.period} onChange={(e) => setEdu(i, "period", e.target.value)}
                        placeholder="e.g. 2018 - 2021" data-testid={`education-period-${i}`}
                        className="flex-1 h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                      <button type="button" onClick={() => setProfile((p) => ({ ...p, education: p.education.filter((_, idx) => idx !== i) }))}
                        data-testid={`education-remove-${i}`}
                        className="h-11 px-4 border border-zinc-300 rounded-md text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors duration-200">
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 pt-3">Your CV &amp; ID</p>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1.5">Link to your CV or LinkedIn</label>
              <input data-testid="profile-cv-link" value={profile.cv_link} onChange={(e) => setProfile({ ...profile, cv_link: e.target.value })}
                placeholder="https://" 
                className="w-full h-12 px-4 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <p className="text-xs text-zinc-400 mt-1.5">We pre-fill this on your applications so you don't have to dig it out every time.</p>
            </div>
            <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="cv-upload-card">
              <p className="text-sm font-semibold text-zinc-950">Your CV file</p>
              <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                {user?.cv_filename
                  ? `Current CV: ${user.cv_filename}. It's attached to every application automatically.`
                  : "Upload your CV once and we'll attach it to every application you send."}
              </p>
              <label className="mt-3 inline-flex items-center gap-2 h-10 px-4 border border-dashed border-zinc-300 rounded-md text-xs font-semibold text-zinc-600 cursor-pointer hover:border-zinc-500 transition-colors duration-200" data-testid="cv-upload">
                <Upload className="h-3.5 w-3.5" /> {cvUploading ? "Uploading…" : user?.cv_filename ? "Replace CV" : "Upload CV (PDF or image)"}
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={uploadCv} disabled={cvUploading} />
              </label>
            </div>
            <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="id-verification-card">
              <div className="flex items-center gap-2 mb-1">
                <BadgeCheck className="h-4 w-4 text-blue-600" />
                <p className="text-sm font-semibold text-zinc-950">ID verification</p>
                {user?.verification_status === "verified" && (
                  <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full" data-testid="id-verified-badge">Verified</span>
                )}
                {user?.verification_status === "pending" && (
                  <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-testid="id-pending-badge">Being reviewed</span>
                )}
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Some employers (especially for driving roles) ask for verified ID. Yours is stored securely, never shared
                publicly, and only used for verification. See the terms.
              </p>
              {(!user?.verification_status || ["none", "rejected"].includes(user.verification_status)) && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 h-10 px-4 border border-dashed border-zinc-300 rounded-md text-xs font-semibold text-zinc-600 cursor-pointer hover:border-zinc-500 transition-colors duration-200" data-testid="id-upload">
                    <Upload className="h-3.5 w-3.5" /> {idUploading ? "Uploading…" : idDoc ? idDoc.filename : "Attach photo ID"}
                    <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={uploadId} disabled={idUploading} />
                  </label>
                  {idDoc && (
                    <button onClick={submitId} disabled={idSaving} data-testid="id-submit"
                      className="h-10 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                      {idSaving ? "Submitting…" : "Submit for verification"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </form>
          </div>
        )}

        {tab === "profile" && (
          <div className="max-w-2xl mt-10">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Notifications</p>
            <NotificationPrefs />
          </div>
        )}

        {tab === "profile" && (
          <div className="max-w-2xl space-y-4 mt-10" data-testid="privacy-tab">
            <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-400">Privacy & data</h2>
            <div className="border border-zinc-200 rounded-md p-5 bg-white">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-zinc-950">Be discoverable to verified employers</p>
                  <p className="text-xs text-zinc-500 mt-1">Verified companies can see your profile (never your email, phone or CV) and invite you to apply. Off by default. Access requests and invites appear under Interviews.</p>
                </div>
                <input type="checkbox" checked={privacyLocal !== null ? privacyLocal : !!user?.discoverable}
                  onChange={(e) => { setPrivacyLocal(e.target.checked); setPrivacy({ discoverable: e.target.checked }).finally(() => setPrivacyLocal(null)); }}
                  className="h-5 w-5 accent-zinc-900" data-testid="discoverable-toggle" />
              </div>
            </div>
            <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="talent-activity-pointer">
              <p className="text-sm font-semibold text-zinc-950">Who's contacted you</p>
              <p className="text-xs text-zinc-500 mt-1">
                Access requests and employer invites now live under the <span className="font-medium text-zinc-700">Interviews</span> tab, where you can approve or decline them.
              </p>
            </div>
            <div className="border border-zinc-200 rounded-md p-5 bg-white">
              <p className="text-sm font-semibold text-zinc-950">Data retention</p>
              <p className="text-xs text-zinc-500 mt-1 mb-3">Choose what happens to your CV and profile details if you stop using HiredToday.</p>
              <select value={user?.data_retention || "keep"} onChange={(e) => setPrivacy({ data_retention: e.target.value })}
                className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm bg-white" data-testid="retention-select">
                <option value="keep">Keep my details while my account is open</option>
                <option value="12m">Remove my CV and profile details after 12 months of inactivity</option>
              </select>
            </div>
            <div className="border border-zinc-200 rounded-md p-5 bg-white" data-testid="profile-actions-card">
              <p className="text-sm font-semibold text-zinc-950">Your profile & CV</p>
              <p className="text-xs text-zinc-500 mt-1 mb-4">Save everything you've edited above, or download your finished CV as a PDF, HiredToday keeps an archived copy for you too.</p>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={saveProfile} disabled={saving} data-testid="cv-save"
                  className="h-11 px-6 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                  {saving ? "Saving…" : "Save profile & CV"}
                </button>
                <button onClick={downloadCvPdf} disabled={pdfBusy} data-testid="cv-download-pdf" type="button"
                  className="h-11 px-6 bg-blue-700 text-white text-xs font-semibold rounded-md hover:bg-blue-800 transition-colors duration-200 disabled:opacity-50 inline-flex items-center gap-2">
                  <Download className="h-3.5 w-3.5" /> {pdfBusy ? "Preparing…" : "Download PDF CV"}
                </button>
                <button onClick={printCv} data-testid="cv-print" type="button"
                  className="h-11 px-4 text-xs font-semibold text-zinc-500 hover:text-zinc-800 transition-colors duration-200 inline-flex items-center gap-1.5">
                  <Printer className="h-3.5 w-3.5" /> Print
                </button>
              </div>
            </div>
            <div className="border border-red-200 rounded-md p-5 bg-red-50/40">
              <p className="text-sm font-semibold text-zinc-950">Delete your account</p>
              <p className="text-xs text-zinc-500 mt-1 mb-3">Your account, applications, saved jobs and CV are permanently erased after 30 days. You can cancel at any point before then.</p>
              {confirmDelete ? (
                <div className="flex gap-2">
                  <button onClick={requestDeletion} disabled={deleteBusy} data-testid="confirm-delete-button"
                    className="h-10 px-5 bg-red-600 text-white text-xs font-semibold rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors duration-200">
                    {deleteBusy ? "Working…" : "Yes, delete my account"}
                  </button>
                  <button onClick={() => setConfirmDelete(false)} data-testid="cancel-delete-confirm-button"
                    className="h-10 px-5 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                    Keep my account
                  </button>
                </div>
              ) : (
                <button onClick={() => setConfirmDelete(true)} data-testid="delete-account-button"
                  className="h-10 px-5 border border-red-300 text-red-700 text-xs font-semibold rounded-md hover:bg-red-50 transition-colors duration-200">
                  Delete my account
                </button>
              )}
            </div>
          </div>
        )}

        {tab === "profile" && (
          <div data-testid="cv-builder" className="mt-10">
            <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Your CV</h2>
            <style>{`@media print { body * { visibility: hidden; } #cv-paper, #cv-paper * { visibility: visible; } #cv-paper { position: absolute; left: 0; top: 0; width: 100%; border: none; } }`}</style>
            <p className="text-sm text-zinc-500 max-w-xl">Your CV builds itself from your profile. Edit your education in the form above, then save and print it or save it as a PDF from your browser.</p>
            <div className="mt-6 max-w-2xl">
              <div id="cv-paper" className="bg-white border border-zinc-200 rounded-md p-8" data-testid="cv-preview">
                <h2 className="font-display text-2xl font-bold text-zinc-950">{profile.name || user?.name || "Your name"}</h2>
                {profile.headline && <p className="text-sm text-zinc-600 mt-1">{profile.headline}</p>}
                <p className="text-xs text-zinc-400 mt-1">{[profile.location, profile.phone, user?.email].filter(Boolean).join(" · ")}</p>
                {profile.about && (
                  <>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mt-6 mb-2">About</h3>
                    <p className="text-sm text-zinc-700 leading-relaxed">{profile.about}</p>
                  </>
                )}
                {profile.skillsText && (
                  <>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mt-6 mb-2">Skills</h3>
                    <p className="text-sm text-zinc-700">{profile.skillsText}</p>
                  </>
                )}
                {profile.experience.length > 0 && (
                  <>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mt-6 mb-2">Experience</h3>
                    {profile.experience.map((x, i) => (
                      <div key={`${x.title}-${x.company || ""}-${x.period || ""}-${i}`} className="mb-3">
                        <p className="text-sm font-semibold text-zinc-900">{x.title}{x.company ? ` · ${x.company}` : ""}</p>
                        <p className="text-xs text-zinc-400">{x.period}</p>
                      </div>
                    ))}
                  </>
                )}
                {profile.education.length > 0 && (
                  <>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mt-6 mb-2">Education</h3>
                    {profile.education.map((x, i) => (
                      <div key={`${x.title}-${x.place || ""}-${x.period || ""}-${i}`} className="mb-3">
                        <p className="text-sm font-semibold text-zinc-900">{x.title}{x.place ? ` · ${x.place}` : ""}</p>
                        <p className="text-xs text-zinc-400">{x.period}</p>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </main>
  );
}
